import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import api from '../api';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Collapse,
  TablePagination,
  Chip
} from '@mui/material';
import {
  DataObject,
  FilterList,
  Clear,
  ExpandMore,
  ExpandLess,
  Person,
  Analytics,
  TrendingUp,
  Storage
} from '@mui/icons-material';

function DatapointActivity() {
  const navigate = useNavigate();
  const { selectedUser, selectUserById } = useUser();

  // Filter states
  const [filters, setFilters] = useState({
    name: '',
    param1: '',
    param2: '',
    dateFrom: '',
    dateTo: '',
    minValue: '',
    maxValue: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Fetch datapoints with filters and pagination for the selected user
  const { data: datapointsData, isLoading, error } = useQuery({
    queryKey: ['userDatapoints', selectedUser?.id, filters, page, rowsPerPage],
    queryFn: async () => {
      if (!selectedUser?.id) return null;
      const params = new URLSearchParams({
        userId: selectedUser.id,
        limit: rowsPerPage.toString(),
        offset: (page * rowsPerPage).toString(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });
      const response = await api.get(`/analytics/datapoints?${params}`);
      return response.data;
    },
    enabled: !!selectedUser?.id,
    staleTime: 30000
  });

  // Get unique values for filter dropdowns
  const uniqueValues = useMemo(() => {
    if (!datapointsData?.dataPoints?.length) return { names: [] };
    
    const names = [...new Set(datapointsData.dataPoints.map(d => d.name).filter(Boolean))];
    
    return { names };
  }, [datapointsData?.dataPoints]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      name: '',
      param1: '',
      param2: '',
      dateFrom: '',
      dateTo: '',
      minValue: '',
      maxValue: ''
    });
    setPage(0);
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };


  if (!selectedUser) {
    return (
      <Box display="flex" justifyContent="center" mt={4} sx={{ p: 2 }}>
        <Typography variant="h6">No user selected. Please select a user from the User Search.</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography>Loading datapoint activity...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load datapoint activity: {error.message}
      </Alert>
    );
  }

  const stats = datapointsData?.stats || {};

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Datapoint Activity - {selectedUser.name}
      </Typography>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <DataObject sx={{ fontSize: 40, color: '#4caf50' }} />
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Total Datapoints
                  </Typography>
                  <Typography variant="h6" color="white">
                    {stats.totalDatapoints || 0}
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    For this user
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Person sx={{ fontSize: 40, color: '#ff9800' }} />
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Unique Names
                  </Typography>
                  <Typography variant="h6" color="white">
                    {stats.uniqueDatapointTypes || 0}
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    Different types
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Storage sx={{ fontSize: 40, color: '#fff' }} />
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Datapoint Types
                  </Typography>
                  <Typography variant="h6" color="white">
                    {stats.uniqueDatapointTypes || 0}
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    Unique types
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <TrendingUp sx={{ fontSize: 40, color: '#fff' }} />
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Average Value
                  </Typography>
                  <Typography variant="h6" color="white">
                    {stats.avgValue || '0.00'}
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    Range: {stats.minValue || '0'} - {stats.maxValue || '0'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Datapoints Table */}
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">
            User Datapoints
            {datapointsData && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({datapointsData.totalCount} datapoints for {selectedUser.name})
              </Typography>
            )}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {hasActiveFilters && (
              <Button
                startIcon={<Clear />}
                onClick={clearFilters}
                size="small"
                color="secondary"
              >
                Clear Filters
              </Button>
            )}
            <Button
              startIcon={<FilterList />}
              endIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowFilters(!showFilters)}
              variant={hasActiveFilters ? "contained" : "outlined"}
              size="small"
            >
              Filters
            </Button>
          </Stack>
        </Stack>

        <Collapse in={showFilters}>
          <Paper variant="outlined" sx={{ 
            p: 2, 
            mb: 2, 
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Grid container spacing={2}>
              {/* Date Filters */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Date From"
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Date To"
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Name Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Datapoint Name</InputLabel>
                  <Select
                    value={filters.name}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                    label="Datapoint Name"
                  >
                    <MenuItem value="">All Names</MenuItem>
                    {uniqueValues.names.map(name => (
                      <MenuItem key={name} value={name}>{name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>



              {/* Param1 Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Param1"
                  value={filters.param1}
                  onChange={(e) => handleFilterChange('param1', e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Search by param1..."
                />
              </Grid>

              {/* Param2 Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Param2"
                  value={filters.param2}
                  onChange={(e) => handleFilterChange('param2', e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Search by param2..."
                />
              </Grid>

              {/* Value Filters */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Min Value"
                  type="number"
                  value={filters.minValue}
                  onChange={(e) => handleFilterChange('minValue', e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Max Value"
                  type="number"
                  value={filters.maxValue}
                  onChange={(e) => handleFilterChange('maxValue', e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ step: 0.01 }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        <Divider sx={{ mb: 2 }} />

        {!datapointsData?.dataPoints?.length ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No datapoints found {hasActiveFilters ? 'matching the current filters' : ''}
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>ID</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>

                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Param1</strong></TableCell>
                    <TableCell><strong>Param2</strong></TableCell>
                    <TableCell><strong>Value</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {datapointsData.dataPoints.map((datapoint) => (
                    <TableRow key={datapoint.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {datapoint.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(datapoint.createdAt)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={datapoint.name || 'N/A'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {datapoint.param1 || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {datapoint.param2 || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {datapoint.value || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={datapointsData.totalCount}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[25, 50, 100, 200, 500]}
              sx={{ mt: 2 }}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}

export default DatapointActivity;